<template>
  <div class="bg-gray-50">
    <div class="brand-site-container flex bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <!-- 左侧：我的品牌站点列表 -->
      <div class="w-80 bg-white flex flex-col">
        <!-- 我的品牌站点标题 + 新建按钮 + 搜索框 -->
        <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">我的站点</h3>
          <div class="flex items-center gap-2">
            <button
              @click="createNewSite"
              :disabled="isCreatingNewSite"
              class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
            <svg v-if="!isCreatingNewSite" class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <div v-else class="animate-spin w-3 h-3 mr-1">
              <svg class="w-3 h-3" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
          </div>
              {{ isCreatingNewSite ? '创建中' : '新建' }}
            </button>
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="搜索..."
                class="w-24 pl-6 pr-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
              <div class="absolute inset-y-0 left-0 pl-1.5 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-3 w-3 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        <!-- 站点列表 -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="loading" class="p-4">
            <div class="animate-pulse space-y-2">
              <div class="h-4 bg-gray-200 rounded"></div>
              <div class="h-4 bg-gray-200 rounded w-4/5"></div>
              <div class="h-4 bg-gray-200 rounded w-3/5"></div>
            </div>
          </div>
          
          <div v-else-if="sites.length === 0" class="p-8 text-center text-gray-500">
            <BuildingStorefrontIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p class="text-sm">{{ searchQuery ? '没有找到匹配的站点' : '暂无品牌站点' }}</p>
            <p class="text-xs text-gray-400 mt-1">
              {{ searchQuery ? '尝试调整搜索条件' : '点击右上角"新建"按钮创建您的第一个品牌站点' }}
          </p>
        </div>

          <div v-else class="divide-y divide-gray-100">
            <div
              v-for="site in sites"
              :key="site.id"
              class="p-4 hover:bg-gray-50 cursor-pointer transition-colors relative"
              :class="{ 
                'bg-blue-50': currentSite?.id === site.id,
                'opacity-50 pointer-events-none': (loadingSiteId && loadingSiteId !== site.id) || deletingSiteId === site.id
              }"
              @click="selectSite(site)"
            >
              <!-- 加载状态覆盖层 -->
              <div v-if="loadingSiteId === site.id" class="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center z-10">
                <div class="animate-spin h-5 w-5 text-blue-600">
                  <svg class="w-5 h-5" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
          </div>
        </div>

              <!-- 删除状态覆盖层 -->
              <div v-if="deletingSiteId === site.id" class="absolute inset-0 bg-red-50 bg-opacity-75 flex items-center justify-center z-10">
                <div class="flex items-center space-x-2">
                  <div class="animate-spin h-5 w-5 text-red-600">
                    <svg class="w-5 h-5" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
              </div>
                  <span class="text-sm text-red-700 font-medium">删除中...</span>
                </div>
            </div>

              <!-- 标题行 -->
              <div class="flex items-start justify-between mb-1">
                <div class="flex-1 mr-2">
                  <h4 class="text-sm font-medium text-gray-900 truncate">{{ site.name }}</h4>
                  <!-- 创建者标签 -->
                  <div v-if="site.created_by_name" class="mt-1">
                    <span :class="[
                      'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                      site.created_by_name === '主账号' ? 'bg-blue-100 text-blue-800' :
                      site.created_by_name === '我的' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    ]">
                      {{ site.created_by_name }}
                    </span>
                  </div>
                </div>
                <div class="flex-shrink-0">
                  <div class="flex space-x-1">
                    <button
                      @click.stop="duplicateSite(site)"
                      :disabled="deletingSiteId === site.id"
                      class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="复制站点"
                    >
                      <DocumentDuplicateIcon class="h-4 w-4" />
                    </button>
                    <button
                      @click.stop="deleteSite(site)"
                      :disabled="deletingSiteId === site.id"
                      class="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="删除站点"
                    >
                      <div v-if="deletingSiteId === site.id" class="animate-spin w-4 h-4">
                        <svg class="w-4 h-4" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
              </div>
                      <TrashIcon v-else class="h-4 w-4" />
                    </button>
              </div>
                </div>
            </div>

              <!-- 描述行 -->
              <p class="text-xs text-gray-400 mt-1 line-clamp-2">{{ site.site_description || '暂无描述' }}</p>
              
              <!-- 统计信息行 -->
              <div class="flex items-center mt-2 text-xs text-gray-400">
                <span>访问 {{ site.visit_count || 0 }} 次</span>
                <span class="mx-2">•</span>
                <span>产品 {{ site.products?.length || 0 }} 个</span>
              </div>

              <!-- 域名状态 -->
              <div v-if="site.custom_domain" class="mt-2 text-xs">
                <span class="text-blue-600 truncate block">{{ site.custom_domain }}</span>
              </div>
            </div>
          </div>
        </div>
            </div>

      <!-- 中间左：站点基本信息面板 -->
      <div class="w-info bg-white border-l border-r border-gray-200 flex flex-col">
        <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">站点信息</h3>
          <button
            @click="saveSite"
            :disabled="isSaving"
            class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <CheckIcon v-if="!isSaving" class="w-3 h-3 mr-1" />
            <div v-else class="animate-spin w-3 h-3 mr-1">
              <svg class="w-3 h-3" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              </div>
            {{ isSaving ? '保存中' : '保存' }}
          </button>
            </div>

        <div class="flex-1 overflow-y-auto p-4 space-y-4">
          <div v-if="!currentSite" class="text-center py-8">
            <div class="text-center text-gray-400">
              <InformationCircleIcon class="h-8 w-8 mx-auto mb-2" />
              <p class="text-sm">选择站点后显示详细信息</p>
              </div>
            </div>

        <div v-else class="space-y-4">
          <!-- 基本信息 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">站点名称 <span class="text-red-500">*</span></label>
            <input
              v-model="formData.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="输入站点名称"
              @input="markDirty"
            />
              </div>

          <!-- SEO -->
          <div class="border-t pt-4">
            
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">页面标题</label>
                <input
                  v-model="formData.site_title"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="SEO标题"
                  @input="markDirty"
                />
            </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">页面描述</label>
                <textarea
                  v-model="formData.site_description"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="SEO描述"
                  @input="markDirty"
                ></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">关键词</label>
                <input
                  v-model="formData.site_keywords"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="品牌,产品,服务"
                  @input="markDirty"
                />
            </div>
          </div>
        </div>

          <!-- 产品搜索 - 完全复制自 QrCodeList.vue -->
          <div v-if="currentSite" class="border-t pt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">搜索产品</label>
            <div class="relative">
              <input
                v-model="productSearchQuery"
                @input="searchProducts($event.target.value)"
                type="text"
                class="w-full px-3 py-2 pl-8 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="输入产品名称或编号..."
              />
              <div class="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-3 w-3 text-gray-400" />
            </div>
              <div v-if="isSearchingProducts" class="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                <svg class="animate-spin h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
              
              <!-- 搜索结果 -->
              <div v-if="productSearchResults.length > 0" class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                <div 
                  v-for="product in productSearchResults" 
                  :key="product.id" 
                  @click="addProduct(product)"
                  class="flex items-center justify-between p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 text-xs truncate">{{ product.name }}</div>
                    <div class="text-xs text-gray-500">
                      <span class="font-mono">{{ product.code }}</span>
                      <span v-if="product.brand" class="ml-2">· {{ product.brand }}</span>
            </div>
            </div>
                  <button class="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                    添加
                  </button>
            </div>
            </div>
          </div>
        </div>

          <!-- 已绑定产品列表 - 完全复制自 QrCodeList.vue -->
          <div v-if="boundProducts.length > 0">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                已绑定产品
              </label>
              <span class="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {{ boundProducts.length }}
              </span>
            </div>

            <div class="space-y-1 max-h-24 overflow-y-auto custom-scrollbar">
              <div 
                v-for="product in boundProducts" 
                :key="product.id"
                class="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded text-xs"
              >
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 truncate">{{ product.name }}</div>
                  <div class="text-xs text-gray-500">
                    <span class="font-mono">{{ product.code }}</span>
                    <span v-if="product.brand" class="ml-2">· {{ product.brand }}</span>
                  </div>
                </div>
                <button 
                  @click="removeProduct(product)"
                  class="ml-2 p-0.5 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                  title="移除产品"
                >
                  <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <!-- 自定义域名配置 -->
          <div class="border-t pt-4 mt-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-900">自定义域名配置</h4>
              <div class="flex gap-2">
                <button
                  v-if="currentSite && !formData.custom_domain"
                  @click="openDomainModal"
                  class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 transition-colors"
                >
                  + 绑定域名
                </button>
                <button
                  v-if="currentSite && formData.custom_domain"
                  @click="clearDomain"
                  class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 transition-colors"
                >
                  清空域名
                </button>
              </div>
            </div>

            <!-- 域名状态卡片 -->
            <div v-if="formData.custom_domain" class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900 mb-2">{{ formData.custom_domain }}</div>
                  <div class="flex items-center gap-3 text-xs">
                    <span :class="domainStatus.verified ? 'text-green-600 font-medium' : 'text-orange-600'">
                      {{ domainStatus.verified ? '✓ 已验证' : '○ 未验证' }}
                    </span>
                    <span :class="domainStatus.ssl_enabled ? 'text-green-600 font-medium' : 'text-gray-500'">
                      {{ domainStatus.ssl_enabled ? '🔒 SSL已启用' : '🔓 待配置SSL' }}
                    </span>
                  </div>
                </div>
                
                <!-- 根据状态显示不同的操作按钮 -->
                <button
                  v-if="!domainStatus.verified"
                  @click="openDomainModal"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  验证域名
                </button>
                <button
                  v-else-if="domainStatus.verified && !domainStatus.ssl_enabled"
                  @click="openDomainModal"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  配置SSL
                </button>
              </div>
            </div>

            <p v-else class="text-xs text-gray-500">
              绑定自定义域名，让用户通过您的品牌域名访问站点
            </p>
          </div>
          
          <!-- 访问链接 -->
          <div v-if="currentSite" class="border-t pt-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-900">访问链接</h4>
              <button
                v-if="currentSite.full_url"
                @click="copyUrl(currentSite.full_url)"
                class="text-xs text-blue-600 hover:text-blue-700"
                title="复制链接"
              >
                复制
              </button>
            </div>

            <div v-if="currentSite.full_url" class="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <a 
                :href="currentSite.full_url" 
                target="_blank"
                class="text-sm text-blue-600 hover:text-blue-700 hover:underline break-all block"
              >
                {{ currentSite.full_url }}
              </a>
              <p class="text-xs text-gray-500 mt-2">
                <svg class="inline w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                点击访问品牌站点
              </p>
            </div>
          </div>
          
          <!-- 站点统计 -->
          <div v-if="currentSite" class="border-t pt-4 mt-4">
            <h4 class="text-sm font-medium text-gray-900 mb-3">站点统计</h4>
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">访问次数:</span>
                <button 
                  @click="openVisitStatsModal"
                  class="font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  :title="'点击查看访问详情'"
                >
                  {{ currentSite.visit_count || 0 }}
                </button>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">联系提交:</span>
                <button 
                  @click="openContactStatsModal"
                  class="font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  :title="'点击查看联系表单详情'"
                >
                  {{ currentSite.contact_count || 0 }}
                </button>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">创建时间:</span>
                <span class="font-medium">{{ formatDateTime(currentSite.created_at) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">更新时间:</span>
                <span class="font-medium">{{ formatDateTime(currentSite.updated_at) }}</span>
              </div>
            </div>
          </div>
            </div>
          </div>
        </div>

      <!-- 中间右：HTML编辑器面板 -->
      <div class="flex-1 flex flex-col bg-white">
        <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">HTML内容编辑</h3>
          <div class="flex items-center space-x-2">
            <button
              v-if="currentSite"
              @click="openFullscreenEditor"
              class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors"
            >
              <CodeBracketIcon class="w-3 h-3 mr-1" />
              全屏
            </button>
            <button
              @click="saveSite"
              :disabled="isSaving"
              class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CheckIcon v-if="!isSaving" class="w-3 h-3 mr-1" />
              <div v-else class="animate-spin w-3 h-3 mr-1">
                <svg class="w-3 h-3" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
              {{ isSaving ? '保存中' : '保存' }}
            </button>
            </div>
            </div>
        
        <div class="flex-1 flex flex-col overflow-hidden">
          <div v-if="!currentSite" class="text-center py-8">
            <div class="text-center text-gray-400">
              <InformationCircleIcon class="h-8 w-8 mx-auto mb-2" />
              <p class="text-gray-500 mb-6 text-sm">从左侧选择现有站点，或从右侧模板库添加新站点</p>
            </div>
          </div>
          <HTMLEditor
            v-else
            ref="htmlEditorInstance"
            v-model="formData.html_content"
            :height="calculateEditorHeight()"
            :preview-src="sitePreviewSrc"
            @change="markDirty"
          />
          </div>
            </div>

      <!-- 右侧：品牌站点模板库 -->
      <div class="w-80 bg-white border-l border-gray-200 flex flex-col">
        <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">品牌站点模板库</h3>
          <div class="relative">
            <input
              v-model="templateSearchQuery"
              type="text"
              placeholder="搜索..."
              class="w-24 pl-6 pr-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
            <div class="absolute inset-y-0 left-0 pl-1.5 flex items-center pointer-events-none">
              <MagnifyingGlassIcon class="h-3 w-3 text-gray-400" />
            </div>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div v-if="loadingTemplates" class="p-4 space-y-4">
            <div v-for="i in 3" :key="`loading-${i}`" class="animate-pulse">
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
          
          <div v-else-if="templates.length === 0" class="p-8 text-center text-gray-500">
            <DocumentTextIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p class="text-sm">{{ templateSearchQuery ? '没有找到匹配的模板' : '暂无可用模板' }}</p>
          </div>
          
          <div v-else class="divide-y divide-gray-100">
            <div
              v-for="template in templates"
              :key="template.id"
              class="p-4 hover:bg-gray-50 transition-colors"
            >
              <div class="space-y-2">
                <h4 class="text-sm font-medium text-gray-900 truncate">{{ template.name }}</h4>
                <p class="text-xs text-gray-500 line-clamp-2">{{ template.description }}</p>
                
                <div class="flex items-center justify-between pt-1">
                  <div class="text-xs text-gray-400">
                    使用 {{ template.usage_count || 0 }} 次
                  </div>
                  <div class="flex space-x-2">
            <button
                      @click="previewTemplate(template)"
                      class="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    >
                      预览
                    </button>
                    <button
                      @click="addTemplateToSite(template)"
                      :disabled="addingTemplateId === template.id"
                      class="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span v-if="addingTemplateId === template.id">添加中...</span>
                      <span v-else>添加</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
      </div>
    </div>
    
    <!-- 全屏HTML编辑器模态框 -->
    <Teleport to="body">
      <div v-if="showFullscreenEditor" class="fixed inset-0 z-50 bg-white">
        <!-- 编辑器头部 -->
        <div class="bg-gray-800 border-b border-gray-700 px-6 py-3 flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <h3 class="text-lg font-medium text-white">HTML 全屏编辑器</h3>
            <span class="text-sm text-gray-400">{{ currentSite?.name }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <button
              @click="saveFullscreenAndClose"
              class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors"
            >
              保存并关闭
            </button>
            <button
              @click="closeFullscreenEditor"
              class="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 text-sm transition-colors"
            >
              取消
            </button>
          </div>
        </div>

        <!-- 编辑器内容 -->
        <div class="h-[calc(100vh-57px)]">
          <HTMLEditor
            ref="fullscreenHtmlEditorInstance"
            v-model="fullscreenHtmlContent"
            :height="'calc(100vh - 57px)'"
            :preview-src="sitePreviewSrc"
          />
        </div>
      </div>
    </Teleport>
    
    <!-- 访问统计模态框 -->
    <VisitStatsModal
      v-model:visible="showVisitStatsModal"
      :item-id="currentSite?.id"
      :item-info="currentSite"
      :api-function="visitStatsApiFunction"
    />
    
    <!-- 联系表单统计模态框 -->
    <ContactStatsModal
      v-model:visible="showContactStatsModal"
      :item-id="currentSite?.id"
      :item-info="currentSite"
      :api-function="contactStatsApiFunction"
    />
    
    <!-- 域名配置模态框 (复用二维码的逻辑) -->
    <Teleport to="body">
      <div v-if="showDomainModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeDomainModal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeDomainModal"></div>
          
          <div class="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full transform transition-all" @click.stop>
            <!-- 域名配置模态框内容 - 与二维码相同 -->
            <div class="px-6 py-4 border-b border-gray-200">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900">自定义域名配置</h3>
                <button @click="closeDomainModal" class="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- 步骤指示器 -->
            <div class="px-6 py-4 bg-gray-50">
              <div class="flex items-center justify-between">
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.verified && modalStep > 1 ? '✓' : '1' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 1 ? 'text-gray-900' : 'text-gray-500']">配置DNS</div>
                    <div class="text-xs text-gray-500 truncate">设置CNAME记录</div>
                  </div>
                </div>
                
                <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 1 ? 'bg-blue-600' : 'bg-gray-200']"></div>
                
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.verified && modalStep > 2 ? '✓' : '2' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 2 ? 'text-gray-900' : 'text-gray-500']">验证域名</div>
                    <div class="text-xs text-gray-500 truncate">检查DNS解析</div>
                  </div>
                </div>
                
                <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 2 ? 'bg-blue-600' : 'bg-gray-200']"></div>
                
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.ssl_enabled ? '✓' : '3' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 3 ? 'text-gray-900' : 'text-gray-500']">启用SSL</div>
                    <div class="text-xs text-gray-500 truncate">申请HTTPS证书</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 主体内容 (步骤1-3的内容与二维码相同，此处省略，可从二维码复制) -->
            <div class="px-6 py-6">
              <p class="text-center text-gray-500">域名配置功能开发中...</p>
            </div>
            
            <!-- 底部按钮 -->
            <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <div></div>
              <button
                @click="closeDomainModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 域名配置模态框 -->
    <Teleport to="body">
      <div v-if="showDomainModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeDomainModal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <!-- 背景遮罩 -->
          <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeDomainModal"></div>
          
          <!-- 模态框内容 -->
          <div class="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full transform transition-all" @click.stop>
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-gray-200">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900">自定义域名配置</h3>
                <button @click="closeDomainModal" class="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- 步骤指示器 -->
            <div class="px-6 py-4 bg-gray-50">
              <div class="flex items-center justify-between">
                <!-- 步骤1 -->
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.verified && modalStep > 1 ? '✓' : '1' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 1 ? 'text-gray-900' : 'text-gray-500']">配置DNS</div>
                    <div class="text-xs text-gray-500 truncate">设置CNAME记录</div>
                  </div>
                </div>
                
                <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 1 ? 'bg-blue-600' : 'bg-gray-200']"></div>
                
                <!-- 步骤2 -->
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.verified && modalStep > 2 ? '✓' : '2' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 2 ? 'text-gray-900' : 'text-gray-500']">验证域名</div>
                    <div class="text-xs text-gray-500 truncate">检查DNS解析</div>
                  </div>
                </div>
                
                <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 2 ? 'bg-blue-600' : 'bg-gray-200']"></div>
                
                <!-- 步骤3 -->
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.ssl_enabled ? '✓' : '3' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 3 ? 'text-gray-900' : 'text-gray-500']">启用SSL</div>
                    <div class="text-xs text-gray-500 truncate">申请HTTPS证书</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 主体内容 -->
            <div class="px-6 py-6">
              <!-- 步骤1：配置DNS -->
              <div v-if="modalStep === 1" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    域名地址 <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="modalDomain"
                    type="text"
                    class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="例如：brand.yourdomain.com"
                  />
                  <p class="text-xs text-gray-500 mt-1">
                    请输入您要绑定的完整域名
                  </p>
                </div>
                
                <div class="border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    DNS配置说明
                  </h5>
                  <div class="space-y-2.5 text-sm text-gray-700">
                    <p><span class="font-medium text-gray-900">1.</span> 登录您的域名DNS服务商管理后台</p>
                    <p><span class="font-medium text-gray-900">2.</span> 添加一条CNAME记录：</p>
                    <div class="bg-gray-50 rounded-lg p-3 mt-2 font-mono text-xs space-y-1.5 border border-gray-200">
                      <div class="flex justify-between">
                        <span class="text-gray-500">类型：</span>
                        <span class="font-semibold text-gray-900">CNAME</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-gray-500">主机记录：</span>
                        <span class="font-semibold text-gray-900">{{ getHostRecord(modalDomain) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-gray-500">记录值：</span>
                        <span class="font-semibold text-blue-600">apis.bubu.im</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-gray-500">TTL：</span>
                        <span class="font-semibold text-gray-900">600（或默认值）</span>
                      </div>
                    </div>
                    <p class="pt-1"><span class="font-medium text-gray-900">3.</span> 等待DNS解析生效（通常5-30分钟）</p>
                    <p><span class="font-medium text-gray-900">4.</span> 点击"下一步"进行验证</p>
                  </div>
                </div>
              </div>
              
              <!-- 步骤2：验证域名 -->
              <div v-if="modalStep === 2" class="space-y-4">
                <div class="py-6">
                  <h4 class="text-base font-semibold text-gray-900 mb-2">验证域名解析</h4>
                  <p class="text-sm text-gray-600 mb-6">
                    我们将检查您的域名CNAME记录是否已正确配置
                  </p>
                  
                  <div class="border border-gray-200 rounded-lg p-4 mb-6">
                    <div class="text-sm space-y-3">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600">域名：</span>
                        <span class="font-semibold text-gray-900">{{ modalDomain || formData.custom_domain }}</span>
                      </div>
                      <div class="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span class="text-gray-600">状态：</span>
                        <span :class="domainStatus.verified ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'">
                          {{ domainStatus.verified ? '已验证' : '待验证' }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    @click="verifyDomainInModal"
                    :disabled="isVerifyingDomain"
                    class="w-full px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <template v-if="isVerifyingDomain">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      验证中
                    </template>
                    <template v-else>验证域名</template>
                  </button>
                  
                  <p class="mt-4 text-xs text-gray-500">
                    如果验证失败，请检查DNS配置是否正确，并等待解析生效
                  </p>
                </div>
              </div>
              
              <!-- 步骤3：启用SSL -->
              <div v-if="modalStep === 3" class="space-y-4">
                <div class="py-6">
                  <h4 class="text-base font-semibold text-gray-900 mb-2">配置SSL证书</h4>
                  <p class="text-sm text-gray-600 mb-6">
                    为您的域名申请免费的HTTPS证书，保护用户数据安全
                  </p>
                  
                  <div class="border border-gray-200 rounded-lg p-4 mb-6">
                    <div class="text-sm space-y-3">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600">域名：</span>
                        <span class="font-semibold text-gray-900">{{ formData.custom_domain }}</span>
                      </div>
                      <div class="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span class="text-gray-600">SSL状态：</span>
                        <span :class="domainStatus.ssl_enabled ? 'text-green-600 font-semibold' : 'text-gray-500 font-semibold'">
                          {{ domainStatus.ssl_enabled ? '已启用' : '待配置' }}
                        </span>
                      </div>
                      <div v-if="domainStatus.ssl_enabled && domainStatus.full_url" class="pt-2 border-t border-gray-100">
                        <span class="text-gray-600 block mb-1.5">访问地址：</span>
                        <a :href="domainStatus.full_url" target="_blank" class="text-blue-600 hover:underline text-xs break-all">
                          {{ domainStatus.full_url }}
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    v-if="!domainStatus.ssl_enabled"
                    @click="applySSLInModal"
                    :disabled="isSettingUpSSL"
                    class="w-full px-6 py-2.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <template v-if="isSettingUpSSL">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      申请中
                    </template>
                    <template v-else>启用SSL</template>
                  </button>
                  
                  <div v-else class="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div class="flex items-start">
                      <svg class="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      <div>
                        <h5 class="text-sm font-semibold text-green-900">SSL证书已成功配置</h5>
                        <p class="text-xs text-green-700 mt-1">您的自定义域名现已支持HTTPS安全访问</p>
                      </div>
                    </div>
                  </div>
                  
                  <p v-if="!domainStatus.ssl_enabled" class="mt-4 text-xs text-gray-500">
                    SSL证书申请需要1-2分钟，请耐心等待
                  </p>
                </div>
              </div>
            </div>
            
            <!-- 底部按钮 -->
            <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <button
                v-if="modalStep > 1 && !domainStatus.ssl_enabled"
                @click="modalStep--"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                上一步
              </button>
              <div v-else></div>
              
              <div class="flex gap-2">
                <button
                  @click="closeDomainModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {{ domainStatus.ssl_enabled && modalStep === 3 ? '完成' : '取消' }}
                </button>
                <button
                  v-if="modalStep === 1"
                  @click="saveAndNextStep"
                  :disabled="!modalDomain || isSaving"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <template v-if="isSaving">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    保存中
                  </template>
                  <template v-else>下一步</template>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 右下角悬浮AI生成按钮 -->
    <button
      @click="showAIGenerator = true"
      class="fixed bottom-10 right-10 z-40 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-purple-700 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-300 transition-all duration-300 group"
      title="使用AI智能生成品牌独立站"
    >
      <svg class="w-4 h-4 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
      <span class="text-sm font-medium whitespace-nowrap">AI生成</span>
      <!-- 脉动光环效果 -->
      <span class="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 animate-slow-ping"></span>
    </button>

    <!-- AI生成向导 -->
    <AIGeneratorModal
      v-model="showAIGenerator"
      @generated="handleAIGenerated"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import {
  BuildingStorefrontIcon, MagnifyingGlassIcon, DocumentDuplicateIcon,
  TrashIcon, CheckIcon, DocumentTextIcon, InformationCircleIcon, CodeBracketIcon
} from '@heroicons/vue/24/outline'
import HTMLEditor from '@/components/common/HTMLEditor.vue'
import AIGeneratorModal from '@/components/brandsite/AIGeneratorModal.vue'
import VisitStatsModal from '@/components/common/VisitStatsModal.vue'
import ContactStatsModal from '@/components/common/ContactStatsModal.vue'
import { brandSiteService } from '@/services/brandSiteService'
import productService from '@/services/productService'

const toast = useToast()

// 编辑器实例引用
const htmlEditorInstance = ref(null)
const fullscreenHtmlEditorInstance = ref(null)

// 计算编辑器高度
const calculateEditorHeight = () => {
  if (typeof window !== 'undefined') {
    const windowHeight = window.innerHeight
    return Math.max(450, windowHeight - 214)
  }
  return 550
}

// 状态管理
const loading = ref(false)
const isSaving = ref(false)
const isDirty = ref(false)
const isCreatingNewSite = ref(false)
const searchQuery = ref('')
const templateSearchQuery = ref('')
const productSearchQuery = ref('')
const allSites = ref([])
const allTemplates = ref([])
const currentSite = ref(null)
const previewTick = ref(0)
const sitePreviewSrc = computed(() => {
  const uuid = currentSite.value?.uuid
  if (!uuid) return ''
  const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
  return `${backendUrl}/brand-sites/${uuid}/?v=${previewTick.value}`
})
const loadingTemplates = ref(false)
const loadingSiteId = ref(null)
const addingTemplateId = ref(null)
const deletingSiteId = ref(null)

// 产品搜索相关状态
const boundProducts = ref([])
const productSearchResults = ref([])
const isSearchingProducts = ref(false)
let searchTimeout = null // 防抖定时器

// 自定义域名相关状态
const domainStatus = ref({
  verified: false,
  ssl_enabled: false,
  full_url: '',
  instructions: {}
})
const isRefreshingDomain = ref(false)
const showDomainModal = ref(false)
const modalStep = ref(1) // 1: 配置DNS, 2: 验证域名, 3: 启用SSL
const modalDomain = ref('')
const isVerifyingDomain = ref(false)
const isSettingUpSSL = ref(false)

// 访问统计模态框
const showVisitStatsModal = ref(false)
const visitStatsApiFunction = (id, page, pageSize) => brandSiteService.getVisits(id, page, pageSize)

// 联系表单统计模态框
const showContactStatsModal = ref(false)
const contactStatsApiFunction = (id, page, pageSize) => brandSiteService.getContacts(id, page, pageSize)

// 全屏编辑器相关状态
const showFullscreenEditor = ref(false)
const fullscreenHtmlContent = ref('')

// AI生成相关状态
const showAIGenerator = ref(false)

// 表单数据
const formData = reactive({
  name: '',
  brand_name: '',
  brand_slogan: '',
  site_title: '',
  site_description: '',
  site_keywords: '',
  html_content: '',
  custom_domain: '',
  contact_email: '',
  contact_phone: '',
  product_ids: []
})

// 计算属性
const isEditing = computed(() => !!(currentSite.value?.id))

// 辅助函数：从域名中提取主机记录
const getHostRecord = (domain) => {
  if (!domain) return 'www'
  
  const parts = domain.split('.')
  
  // 如果只有2段（如 example.com），返回域名本身
  if (parts.length <= 2) {
    return domain
  }
  
  // 如果有3段或以上（如 www.example.com, review.yourbrand.com）
  // 主机记录是除了最后两段（主域名）之外的所有部分
  const hostRecord = parts.slice(0, -2).join('.')
  return hostRecord || 'www'
}

// 前端搜索过滤
const sites = computed(() => {
  const sitesArray = Array.isArray(allSites.value) ? allSites.value : []
  
  if (!searchQuery.value) {
    return sitesArray
  }
  return sitesArray.filter(site => 
    site && site.name && site.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const templates = computed(() => {
  const templatesArray = Array.isArray(allTemplates.value) ? allTemplates.value : []
  
  if (!templateSearchQuery.value) {
    return templatesArray
  }
  return templatesArray.filter(template => 
    template && template.name && template.name.toLowerCase().includes(templateSearchQuery.value.toLowerCase())
  )
})

// 产品搜索方法（带防抖）- 和 QrCodeList.vue 完全一样
const searchProducts = (query) => {
  // 清除之前的定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  
  if (!query || query.trim() === '') {
    productSearchResults.value = []
    isSearchingProducts.value = false
    return
  }

  // 防抖：延迟执行搜索
  searchTimeout = setTimeout(async () => {
    isSearchingProducts.value = true
    
    try {
      // 使用真实API搜索产品
      const result = await productService.getProductSuggestions(query.trim(), 10)
      
      if (result.success) {
        // 过滤已经绑定的产品和非活跃产品
        const boundProductIds = boundProducts.value.map(p => p.id)
        const filteredProducts = result.data.filter(product => 
          !boundProductIds.includes(product.id) && product.is_active !== false
        )
        
        productSearchResults.value = filteredProducts
      } else {
        productSearchResults.value = []
      }
    } catch (error) {
      console.error('产品搜索失败:', error)
      productSearchResults.value = []
      
      // 只有在非取消错误时才显示错误提示
      if (error.name !== 'CanceledError') {
        toast.error('搜索产品失败，请重试')
      }
    } finally {
      isSearchingProducts.value = false
    }
  }, 300) // 300ms 防抖延迟
}

const addProduct = (product) => {
  if (boundProducts.value.find(p => p.id === product.id)) {
    return
  }

  boundProducts.value.push(product)
  productSearchResults.value = productSearchResults.value.filter(p => p.id !== product.id)
  
  // 更新formData中的产品IDs
  if (!formData.product_ids) {
    formData.product_ids = []
  }
  formData.product_ids.push(product.id)
  
  markDirty()
  toast.success(`已添加产品 ${product.name}`)
}

const removeProduct = (product) => {
  boundProducts.value = boundProducts.value.filter(p => p.id !== product.id)
  
  // 从formData中移除产品ID
  if (formData.product_ids) {
    formData.product_ids = formData.product_ids.filter(id => id !== product.id)
  }
  
  markDirty()
  toast.success(`已移除产品 ${product.name}`)
}

// 方法
const loadSites = async () => {
  loading.value = true
  try {
    const response = await brandSiteService.getSites()
    if (response.success) {
      const data = response.data || response.results || []
      allSites.value = Array.isArray(data) ? data : []
    } else {
      allSites.value = []
    }
  } catch (error) {
    toast.error('加载品牌站点失败')
    console.error('加载品牌站点失败:', error)
    allSites.value = []
  } finally {
    loading.value = false
  }
}

const loadTemplates = async () => {
  loadingTemplates.value = true
  try {
    const response = await brandSiteService.getTemplates()
    if (response.success) {
      const data = response.data || response.results || []
      allTemplates.value = Array.isArray(data) ? data : []
    } else {
      allTemplates.value = []
    }
  } catch (error) {
    console.error('加载模板失败:', error)
    allTemplates.value = []
  } finally {
    loadingTemplates.value = false
  }
}

const createNewSite = async () => {
  isCreatingNewSite.value = true
  try {
    const now = new Date()
    const timeStr = now.toLocaleString('zh-CN', { 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit', 
      minute: '2-digit'
    }).replace(/[\/\s:]/g, '')
    
    const newSiteData = {
      name: `新品牌站点_${timeStr}`,
      brand_name: '我的品牌',
      site_title: '我的品牌站点',
      site_description: '欢迎访问我的品牌站点',
      html_content: '',
      use_custom_html: true,
      is_active: true
    }
    
    const response = await brandSiteService.createSite(newSiteData)
    
    if (response.success) {
      toast.success('站点创建成功')
      await loadSites()
      // 自动选择新创建的站点
      selectSite(response.data)
    } else {
      toast.error(response.message || '创建站点失败')
    }
  } catch (error) {
    console.error('创建站点失败:', error)
    toast.error('创建站点失败，请稍后重试')
  } finally {
    isCreatingNewSite.value = false
  }
}

const selectSite = async (site) => {
  loadingSiteId.value = site.id
  
  try {
    const response = await brandSiteService.getSite(site.id)
    
    if (response.success) {
      currentSite.value = response.data
      fillForm(response.data)
      isDirty.value = false
      
      // 如果站点有自定义域名，刷新域名状态
      if (response.data.custom_domain) {
        await refreshDomainStatus()
      } else {
        // 重置域名状态
        domainStatus.value = {
          verified: false,
          ssl_enabled: false,
          full_url: '',
          instructions: {}
        }
      }
    } else {
      toast.error(response.message || '获取站点详情失败')
    }
  } catch (error) {
    toast.error('获取站点详情失败')
    console.error('获取站点详情失败:', error)
  } finally {
    loadingSiteId.value = null
  }
}

const fillForm = (site) => {
  formData.name = site.name || ''
  formData.brand_name = site.brand_name || ''
  formData.brand_slogan = site.brand_slogan || ''
  formData.site_title = site.site_title || ''
  formData.site_description = site.site_description || ''
  formData.site_keywords = site.site_keywords || ''
  formData.html_content = site.html_content || ''
  formData.custom_domain = site.custom_domain || ''
  formData.contact_email = site.contact_email || ''
  formData.contact_phone = site.contact_phone || ''
  
  // 处理产品绑定 - 和 QrCodeList.vue 一样
  formData.product_ids = site.products ? site.products.map(p => p.id) : []
  boundProducts.value = site.products ? [...site.products] : []
  
  // 更新域名状态
  domainStatus.value = {
    verified: site.domain_verified || false,
    ssl_enabled: site.ssl_enabled || false,
    full_url: site.full_url || '',
    instructions: {}
  }
}

const markDirty = () => {
  isDirty.value = true
}

const saveSite = async () => {
  if (!formData.name) {
    toast.error('请填写站点名称')
    return
  }

  isSaving.value = true
  try {
    // 从编辑器获取清理后的HTML（一次性清除所有编辑器标记）
    if (htmlEditorInstance.value && htmlEditorInstance.value.getCleanHTML) {
      formData.html_content = htmlEditorInstance.value.getCleanHTML()
    }
    
    const siteData = { ...formData }
    
    let response
    if (isEditing.value) {
      response = await brandSiteService.updateSite(currentSite.value.id, siteData)
    } else {
      response = await brandSiteService.createSite(siteData)
    }
    
    if (response.success) {
      currentSite.value = response.data
      previewTick.value += 1
      isDirty.value = false
      await loadSites()
      toast.success('保存成功')
    } else {
      toast.error(response.message || '保存失败')
    }
  } catch (error) {
    toast.error('保存失败，请稍后重试')
    console.error('保存站点失败:', error)
  } finally {
    isSaving.value = false
  }
}

const duplicateSite = async (site) => {
  try {
    const response = await brandSiteService.duplicateSite(site.id)
    if (response.success) {
      toast.success('站点复制成功')
      await loadSites()
      selectSite(response.data)
    } else {
      toast.error(response.message || '复制失败')
    }
  } catch (error) {
    toast.error('复制失败，请稍后重试')
    console.error('复制站点失败:', error)
  }
}

const deleteSite = async (site) => {
  if (!confirm(`确定要删除站点"${site.name}"吗？`)) {
    return
  }

  deletingSiteId.value = site.id
  try {
    const response = await brandSiteService.deleteSite(site.id)
    if (response.success) {
      toast.success('站点删除成功')
      if (currentSite.value?.id === site.id) {
        currentSite.value = null
      }
      await loadSites()
    } else {
      toast.error(response.message || '删除失败')
    }
  } catch (error) {
    toast.error('删除失败，请稍后重试')
    console.error('删除站点失败:', error)
  } finally {
    deletingSiteId.value = null
  }
}

const previewTemplate = (template) => {
  try {
    // 构建模板预览URL - 直接使用后端API地址
    const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const previewUrl = `${backendUrl}/brand-sites/template-preview/${template.id}/`
    
    // 品牌站桌面模式预览窗口尺寸 - 大屏展示
    const desktopWidth = 1400   // 桌面宽度
    const desktopHeight = 900   // 桌面高度
    
    // 计算居中位置
    const screenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX
    const screenTop = window.screenTop !== undefined ? window.screenTop : window.screenY
    const width = window.innerWidth || document.documentElement.clientWidth || screen.width
    const height = window.innerHeight || document.documentElement.clientHeight || screen.height
    
    const left = (width / 2) - (desktopWidth / 2) + screenLeft
    const top = (height / 2) - (desktopHeight / 2) + screenTop
    
    // 在新窗口打开预览 - 桌面模式
    const previewWindow = window.open(
      previewUrl, 
      'brandsite_template_preview',
      `width=${desktopWidth},height=${desktopHeight},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
    )
    
    // 检查是否成功打开窗口
    if (!previewWindow) {
      toast.error('无法打开预览窗口，请检查浏览器弹窗设置')
      return
    }
    
    // 设置窗口标题
    previewWindow.addEventListener('load', () => {
      try {
        // 设置窗口标题
        previewWindow.document.title = `🌐 ${template.name} - 桌面端预览`
      } catch (e) {
        // 跨域限制时静默失败
        console.log('预览窗口样式设置受跨域限制')
      }
    })
    
    // 监听窗口加载失败
    previewWindow.addEventListener('error', () => {
      toast.error('模板预览加载失败')
    })
    
  } catch (error) {
    console.error('预览模板失败:', error)
    toast.error('预览功能暂时不可用')
  }
}

const addTemplateToSite = async (template) => {
  if (addingTemplateId.value) return
  
  addingTemplateId.value = template.id
  
  try {
    // 先获取模板详情（包含 HTML 内容）
    const templateDetailResponse = await brandSiteService.getTemplate(template.id)
    
    if (!templateDetailResponse.success) {
      toast.error(templateDetailResponse.message || '获取模板详情失败')
      return
    }
    
    const templateDetail = templateDetailResponse.data
    
    const now = new Date()
    const timeStr = now.toLocaleString('zh-CN', { 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit', 
      minute: '2-digit'
    }).replace(/[\/\s:]/g, '')
    
    const newSiteData = {
      name: `${templateDetail.name}_${timeStr}`,
      brand_name: templateDetail.name,
      site_title: templateDetail.name,
      site_description: `基于 ${templateDetail.name} 模板创建的品牌站点`,
      template: templateDetail.id,
      html_content: templateDetail.html_content || '',
      product_ids: []
    }
    
    const response = await brandSiteService.createSite(newSiteData)
    
    if (response.success) {
      toast.success('模板添加成功')
      await loadSites()
      selectSite(response.data)
    } else {
      toast.error(response.message || '模板添加失败')
    }
  } catch (error) {
    console.error('创建站点失败:', error)
    toast.error('模板添加失败，请稍后重试')
  } finally {
    addingTemplateId.value = null
  }
}

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('zh-CN')
}

const copyUrl = async (url) => {
  try {
    await navigator.clipboard.writeText(url)
    toast.success('链接已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    toast.error('复制失败，请手动复制')
  }
}

// 全屏编辑器相关方法
const openFullscreenEditor = () => {
  if (!currentSite.value) {
    toast.error('请先选择一个站点')
    return
  }
  
  fullscreenHtmlContent.value = formData.html_content || ''
  showFullscreenEditor.value = true
}

const saveFullscreenAndClose = () => {
  // 从全屏编辑器获取清理后的HTML
  if (fullscreenHtmlEditorInstance.value && fullscreenHtmlEditorInstance.value.getCleanHTML) {
    formData.html_content = fullscreenHtmlEditorInstance.value.getCleanHTML()
  } else {
    formData.html_content = fullscreenHtmlContent.value
  }
  markDirty()
  toast.success('HTML内容已更新')
  closeFullscreenEditor()
}

const closeFullscreenEditor = () => {
  showFullscreenEditor.value = false
}

// AI生成相关方法
const handleAIGenerated = async (site) => {
  // 刷新站点列表
  await loadSites()
  
  // 选中AI生成的站点
  const generatedSite = allSites.value.find(s => s.id === site.id)
  if (generatedSite) {
    await selectSite(generatedSite)
    toast.success('AI生成成功！站点已自动选中，您可以继续编辑')
  }
}

// 域名相关方法
const refreshDomainStatus = async () => {
  if (!currentSite.value?.id) return
  
  isRefreshingDomain.value = true
  try {
    const response = await brandSiteService.getDomainStatus(currentSite.value.id)
    if (response.success) {
      domainStatus.value = {
        verified: response.data.verified || false,  // 修正字段名
        ssl_enabled: response.data.ssl_enabled || false,
        full_url: response.data.full_url || '',
        instructions: response.data.instructions || {}
      }
    }
  } catch (error) {
    console.error('获取域名状态失败:', error)
  } finally {
    isRefreshingDomain.value = false
  }
}

const openDomainModal = async () => {
  if (!currentSite.value) {
    toast.error('请先选择一个站点')
    return
  }
  
  // 根据当前站点的域名状态决定从哪一步开始
  if (formData.custom_domain) {
    // 已有域名，填充到模态框
    modalDomain.value = formData.custom_domain
    
    if (currentSite.value.domain_verified && !currentSite.value.ssl_enabled) {
      // 域名已验证但SSL未配置 → 直接跳到第3步
      modalStep.value = 3
      await refreshDomainStatus()
    } else if (currentSite.value.domain_verified && currentSite.value.ssl_enabled) {
      // 域名已验证且SSL已配置 → 从第1步开始（用户可能想更换域名）
      modalStep.value = 1
    } else {
      // 域名未验证 → 从第2步开始
      modalStep.value = 2
    }
  } else {
    // 没有域名 → 从第1步开始
    modalDomain.value = ''
    modalStep.value = 1
  }
  
  showDomainModal.value = true
}

const closeDomainModal = () => {
  showDomainModal.value = false
  // 清空模态框中的域名输入
  modalDomain.value = ''
  // 重置步骤
  modalStep.value = 1
}

const saveAndNextStep = async () => {
  if (!modalDomain.value) {
    toast.error('请输入域名')
    return
  }
  
  if (!currentSite.value?.id) {
    toast.error('请先选择一个站点')
    return
  }
  
  // 第一步：检查域名是否已被占用
  isSaving.value = true
  try {
    const response = await brandSiteService.checkDomain(currentSite.value.id, modalDomain.value)
    
    if (response.success && response.available) {
      // 域名可用，进入第二步
      modalStep.value = 2
    } else {
      // 域名已被占用
      toast.error(response.message || '该域名已被占用')
    }
  } catch (error) {
    console.error('检查域名失败:', error)
    toast.error('检查域名失败，请稍后重试')
  } finally {
    isSaving.value = false
  }
}

const verifyDomainInModal = async () => {
  if (!currentSite.value?.id) {
    toast.error('请先保存站点')
    return
  }
  
  if (!modalDomain.value) {
    toast.error('请输入域名')
    return
  }
  
  isVerifyingDomain.value = true
  try {
    // 第一步：临时保存域名到数据库（用于DNS验证）
    const siteData = { 
      ...formData,
      custom_domain: modalDomain.value
    }
    const saveResponse = await brandSiteService.updateSite(currentSite.value.id, siteData)
    
    if (!saveResponse.success) {
      toast.error(saveResponse.message || '保存域名失败')
      return
    }
    
    // 第二步：验证DNS
    const response = await brandSiteService.verifyDomain(currentSite.value.id)
    
    // 严格检查：success 必须为 true 且 data.verified 也为 true
    if (response.success === true && response.data?.verified === true) {
      // DNS验证成功，手动更新 currentSite 的验证状态（不触发 fillForm）
      if (currentSite.value) {
        currentSite.value.domain_verified = true
        currentSite.value.domain_verified_at = response.data.verified_at
        currentSite.value.custom_domain = modalDomain.value
      }
      
      // 刷新域名状态
      await refreshDomainStatus()
      
      // ⚠️ 注意：不更新前端的 formData.custom_domain，等待SSL申请成功后再更新
      toast.success('DNS验证成功，请继续申请SSL')
      modalStep.value = 3 // 进入第三步（申请SSL）
    } else {
      // 验证失败，清空数据库中的域名
      const clearData = { ...formData, custom_domain: '' }
      await brandSiteService.updateSite(currentSite.value.id, clearData)
      await selectSite(currentSite.value)
      
      // 显示具体的错误信息
      const errorMsg = response.message || response.data?.message || response.error || '域名验证失败，请检查DNS配置'
      toast.error(errorMsg)
      console.error('域名验证失败:', response)
    }
  } catch (error) {
    console.error('验证域名失败:', error)
    toast.error('验证域名失败，请稍后重试')
  } finally {
    isVerifyingDomain.value = false
  }
}

const applySSLInModal = async () => {
  if (!currentSite.value?.id) return
  
  isSettingUpSSL.value = true
  try {
    const response = await brandSiteService.applySSL(currentSite.value.id)
    
    if (response.success) {
      toast.success('SSL证书申请已提交，预计需要1-2分钟')
      
      // 开始轮询检查SSL状态
      let retryCount = 0
      const maxRetries = 20
      
      const checkSSLStatus = setInterval(async () => {
        retryCount++
        
        if (retryCount > maxRetries) {
          clearInterval(checkSSLStatus)
          isSettingUpSSL.value = false
          
          // SSL申请超时，清空域名配置
          toast.error('SSL证书申请超时，请稍后重试')
          const clearData = { ...formData, custom_domain: '' }
          await brandSiteService.updateSite(currentSite.value.id, clearData)
          await selectSite(currentSite.value)
          
          // 关闭模态框
          closeDomainModal()
          return
        }
        
        const statusResponse = await brandSiteService.getDomainStatus(currentSite.value.id)
        if (statusResponse.success && statusResponse.data.ssl_enabled) {
          clearInterval(checkSSLStatus)
          isSettingUpSSL.value = false
          
          // SSL成功启用，现在才更新前端的 formData.custom_domain
          formData.custom_domain = modalDomain.value
          isDirty.value = false
          
          toast.success('SSL证书已成功启用，域名配置完成！')
          await refreshDomainStatus()
          await selectSite(currentSite.value)
          
          // 关闭模态框
          closeDomainModal()
        }
      }, 6000)
    } else {
      // SSL申请失败，清空域名配置
      toast.error(response.message || 'SSL证书申请失败')
      const clearData = { ...formData, custom_domain: '' }
      await brandSiteService.updateSite(currentSite.value.id, clearData)
      await selectSite(currentSite.value)
      
      isSettingUpSSL.value = false
    }
  } catch (error) {
    console.error('申请SSL失败:', error)
    toast.error('申请SSL失败，请稍后重试')
    
    // SSL申请异常，清空域名配置
    const clearData = { ...formData, custom_domain: '' }
    await brandSiteService.updateSite(currentSite.value.id, clearData)
    await selectSite(currentSite.value)
    
    isSettingUpSSL.value = false
  }
}

const clearDomain = async () => {
  if (!currentSite.value) return
  
  if (!confirm(`确定要清空自定义域名吗？\n\n当前域名：${formData.custom_domain}\n\n清空后，相关的DNS验证和SSL证书配置将被重置。`)) {
    return
  }
  
  try {
    formData.custom_domain = ''
    await saveSite()
    
    domainStatus.value = {
      verified: false,
      ssl_enabled: false,
      full_url: '',
      instructions: {}
    }
    
    await selectSite(currentSite.value)
    toast.success('域名已清空')
  } catch (error) {
    console.error('清空域名失败:', error)
    toast.error('清空域名失败，请稍后重试')
  }
}

// 打开访问统计模态框
const openVisitStatsModal = () => {
  if (!currentSite.value) {
    toast.error('请先选择一个站点')
    return
  }
  showVisitStatsModal.value = true
}

// 打开联系表单统计模态框
const openContactStatsModal = () => {
  if (!currentSite.value) {
    toast.error('请先选择一个站点')
    return
  }
  showContactStatsModal.value = true
}

// 生命周期
onMounted(async () => {
  await Promise.all([
    loadSites(),
    loadTemplates()
  ])
  // 注意：不需要预加载产品，产品通过搜索动态加载
})

// 组件销毁时清理资源
onUnmounted(() => {
  // 清除产品搜索定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
})
</script>

<style scoped>
/* 自定义滚动条 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 文本截断样式 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 自定义宽度 */
.w-info {
  width: 24rem;
}

/* 自适应高度计算 */
.brand-site-container {
  height: calc(100vh - 116px - 2rem);
}

@media (min-width: 768px) {
  .brand-site-container {
    height: calc(100vh - 102px - 3rem);
  }
}

/* 4栏布局优化 */
@media (max-width: 1600px) {
  .w-80 {
    width: 18rem;
  }
  .w-info {
    width: 20rem;
  }
}

@media (max-width: 1400px) {
  .brand-site-container > .w-80:last-child {
    display: none;
  }
  .w-80 {
    width: 20rem;
  }
  .w-info {
    width: 22rem;
  }
}

@media (max-width: 1024px) {
  .w-80 {
    width: 16rem;
  }
  .w-info {
    width: 20rem;
  }
}

/* AI按钮慢速脉冲动画 */
@keyframes slow-ping {
  0% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.1;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

.animate-slow-ping {
  animation: slow-ping 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
